<template>
  <button
    type="button"
    class="fg-sailing"
    :class="{ 'is-selected': selected }"
    @click="$emit('select')"
  >
    <div class="fg-sailing__left">
      <div class="fg-sailing__head">
        <img v-if="logo" :src="logo" :alt="companyName" class="fg-sailing__logo" />
        <div class="fg-sailing__id">
          <span class="fg-sailing__company">{{ companyName }}</span>
          <span v-if="shipName && shipName !== companyName" class="fg-sailing__ship">
            <Icon name="ship" :size="11" class="fg-sailing__ship-icon" />{{ shipName }}
          </span>
        </div>
        <span v-if="isCheapest" class="fg-sailing__badge fg-sailing__badge--cheapest">{{ t('results.cheapest') }}</span>
        <span v-else-if="isFastest" class="fg-sailing__badge fg-sailing__badge--fastest">{{ t('results.fastest') }}</span>
      </div>

      <div class="fg-sailing__times">
        <!-- Heure de départ seule au-dessus (pas d'heure d'arrivée : peu fiable
             côté providers). La durée vit dans la bulle du rail. -->
        <span class="fg-sailing__time">{{ formatTime(sailing.primary.depart_at, locale) }}</span>
        <!-- Les deux ports sur la même ligne de base, reliés par le rail :
             "Algeciras ──1h── Tanger med" se lit d'un coup d'œil. -->
        <div class="fg-sailing__route">
          <span class="fg-sailing__port">{{ formatPort(sailing.primary.depart_name) }}</span>
          <div class="fg-sailing__rail">
            <span v-if="durationLabel" class="fg-sailing__rail-bubble">
              <Icon name="clock" :size="11" />
              <span>{{ durationLabel }}</span>
            </span>
          </div>
          <span class="fg-sailing__port fg-sailing__port--to">{{ formatPort(sailing.primary.arrive_name) }}</span>
        </div>
      </div>
    </div>

    <!-- Prix "à partir de" volontairement discret (pas de gros montant en
         évidence) : c'est un repère de comparaison entre horaires, PAS le prix
         final — le tarif réel dépend de la place choisie ensuite. -->
    <div v-if="!selected" class="fg-sailing__right">
      <span v-if="startingPrice" class="fg-sailing__from">
        {{ t('results.startingFrom') }} {{ formatPrice(startingPrice.amount, startingPrice.currency, locale) }}
      </span>
      <span class="fg-sailing__choose">
        {{ t('results.chooseSailing') }}
        <Icon name="chevron-right" :size="14" />
      </span>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '../../i18n'
import { cheapestAvailability, pickCheapestTariff, tariffDisplayAmount, tariffDisplayCurrency } from '../../../../shared/rules'
import { formatDuration, formatPort, formatPrice, formatTime, humanizeLabel } from '../../utils/format'
import Icon from '../Icon.vue'
import type { Sailing } from '../Results.vue'

const props = withDefaults(
  defineProps<{
    sailing: Sailing
    selected: boolean
    locale: string
    direction?: 'outward' | 'return'
    isFastest?: boolean
    isCheapest?: boolean
  }>(),
  { isFastest: false, isCheapest: false },
)
defineEmits<{ select: [] }>()

const { t } = useI18n()

// Prix "à partir de" = tarif ticket le moins cher toutes places confondues du
// sailing (avant que l'user ait choisi sa place). Réutilise les mêmes règles
// pures que PlaceList (cheapestAvailability + pickCheapestTariff) : aucune
// divergence possible avec le prix qui s'affichera ensuite au choix de place.
const startingPrice = computed<{ amount: number; currency: string } | null>(() => {
  const av = cheapestAvailability(props.sailing.all)
  const tariff = av ? pickCheapestTariff(av) : null
  return tariff ? { amount: tariffDisplayAmount(tariff), currency: tariffDisplayCurrency(tariff) } : null
})

// Casse humanisée ("Fantastic" / "Grandinaviveloci") — même rendu que la
// sidebar récap et la page Récapitulatif, fini le SHOUTING provider.
// `humanizeLabel` ne sentence-case que les libellés TOUT-MAJUSCULE ("FANTASTIC"
// → "Fantastic") ; un nom de compagnie tout-minuscule ("balearia") passait tel
// quel. On force la capitale initiale pour un affichage propre ("Balearia").
const companyName = computed(() => capitalizeFirst(humanizeLabel(props.sailing.primary.company?.name)))
const shipName = computed(() => capitalizeFirst(humanizeLabel(props.sailing.primary.ship_name)) || companyName.value)
const logo = computed(() => (props.sailing.primary.company as { logo?: string } | undefined)?.logo ?? '')

const durationLabel = computed(() => formatDuration(props.sailing.primary.duration))

function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}
</script>
